import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Eyebrow, FadeUp, MaskedLine } from "@/components/motion";

const inputCls =
  "w-full rounded-xl border border-brass/20 bg-ink/70 px-4 py-3.5 text-base text-ivory placeholder:text-ivory/35 transition-colors focus:border-brass focus:outline-none";
const labelCls = "mb-2 block text-sm font-medium text-ivory/80";

const Contact = () => {
  const [form, setForm] = useState({ first_name: "", email: "", message: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please share your first name, email and your query.");
      return;
    }
    setState("sending");
    try {
      await api.post("/contact", {
        first_name: form.first_name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setState("done");
    } catch (err) {
      setError(formatApiError(err));
      setState("idle");
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="lamp-glow mx-auto max-w-7xl px-5 pb-14 pt-36 sm:px-8 sm:pt-44">
        <Eyebrow suit="♣">Contact the Lounge</Eyebrow>
        <h1 className="mt-4 font-serif text-5xl leading-[1.02] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.1}>A quiet word,</MaskedLine>
          <MaskedLine delay={0.25} className="italic text-brass">
            whenever you need.
          </MaskedLine>
        </h1>
        <FadeUp delay={0.4}>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ivory/70">
            Questions, concerns or anything you'd rather ask privately — every message is read and
            handled discreetly.
          </p>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 sm:pb-32">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <FadeUp>
            <AnimatePresence mode="wait">
              {state === "done" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  data-testid="contact-success"
                  className="rounded-2xl border border-brass/25 bg-charcoal/60 p-10"
                >
                  <CheckCircle2 size={36} className="text-brass" aria-hidden="true" />
                  <h2 className="mt-5 font-serif text-3xl text-ivory">
                    Message <span className="italic text-brass">received.</span>
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-ivory/70">
                    Thank you for getting in touch. We'll come back to you as soon as we can — keep
                    an eye on your inbox.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  data-testid="contact-form"
                  className="space-y-6 rounded-2xl border border-brass/15 bg-charcoal/50 p-7 sm:p-10"
                  exit={{ opacity: 0, y: -12 }}
                >
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-first-name" className={labelCls}>
                        First name <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="contact-first-name"
                        data-testid="contact-first-name-input"
                        type="text"
                        autoComplete="given-name"
                        value={form.first_name}
                        onChange={set("first_name")}
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className={labelCls}>
                        Email <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="contact-email"
                        data-testid="contact-email-input"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={set("email")}
                        className={inputCls}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-message" className={labelCls}>
                      Your query <span className="text-burgundy">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      rows={6}
                      value={form.message}
                      onChange={set("message")}
                      className={`${inputCls} resize-y`}
                      required
                    />
                  </div>
                  {error && (
                    <p role="alert" data-testid="contact-error" className="rounded-lg bg-burgundy/20 px-4 py-3 text-sm text-ivory">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={state === "sending"}
                    className="w-full rounded-xl bg-burgundy py-4 text-base font-semibold text-ivory transition-all duration-200 hover:bg-[#a33847] hover:shadow-[0_0_30px_rgba(140,46,59,0.5)] disabled:opacity-60 sm:w-auto sm:px-12"
                  >
                    {state === "sending" ? "Sending…" : "Send your query"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </FadeUp>

          <FadeUp delay={0.12}>
            <aside className="rounded-2xl border border-brass/15 bg-charcoal/40 p-8 sm:p-10">
              <Mail size={26} className="text-brass" aria-hidden="true" />
              <h2 className="mt-4 font-serif text-2xl text-ivory">Prefer to email directly?</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-ivory/65">
                You can reach the Lounge any time at:
              </p>
              <a
                href="mailto:hello@playersloungesyd.com"
                data-testid="contact-direct-email-link"
                className="mt-4 inline-block break-all rounded-xl border border-brass/25 px-5 py-3.5 font-serif text-lg italic text-brass transition-colors hover:border-brass hover:bg-brass/10"
              >
                hello@playersloungesyd.com
              </a>
              <p className="mt-6 text-xs leading-relaxed text-ivory/45">
                We treat every enquiry as confidential. Nothing you send is shared beyond the hosts.
              </p>
            </aside>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Contact;
